import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Slider,
  IconButton,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { PlayArrow, Pause } from "@mui/icons-material";

interface AudioPlayerProps {
  src: string;
  showLoopControls?: boolean;
}

// Format time to HH:MM:SS.mm (2-digit ms)
const formatTime = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const milliseconds = Math.floor((ms % 1000) / 10);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )}:${String(seconds).padStart(2, "0")}.${String(milliseconds).padStart(
    2,
    "0"
  )}`;
};

// Parse HH:MM:SS.mm to ms
const parseTime = (timeStr: string): number => {
  const match = timeStr.match(/^(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,2}))?$/);
  if (!match) return 0;
  const [, hh, mm, ss, ms = "0"] = match;
  return (
    parseInt(hh) * 3600000 +
    parseInt(mm) * 60000 +
    parseInt(ss) * 1000 +
    parseInt(ms.padEnd(2, "0")) * 10
  );
};

// Normalize partial input like "0:0:5.2" ➝ "00:00:05.20"
const normalizeTimeInput = (input: string): string => {
  const parts = input.split(":");
  while (parts.length < 3) parts.unshift("0");
  const [hh, mm, rest] = parts;
  const [ss, ms = "00"] = rest.split(".");
  return `${hh.padStart(2, "0")}:${mm.padStart(2, "0")}:${ss.padStart(
    2,
    "0"
  )}.${ms.padEnd(2, "0")}`;
};

// Auto-insert formatting as user types
const autoFormatTime = (input: string): string => {
  const digits = input.replace(/\D/g, "").slice(0, 8); // Max 8 digits
  let formatted = "";
  if (digits.length >= 2) formatted += digits.slice(0, 2) + ":";
  if (digits.length >= 4) formatted += digits.slice(2, 4) + ":";
  if (digits.length >= 6) formatted += digits.slice(4, 6) + ".";
  formatted += digits.slice(6);
  return formatted;
};

const isValidTimeFormat = (input: string): boolean => {
  return /^(\d{1,2}):(\d{1,2}):(\d{1,2})(\.\d{1,2})?$/.test(input);
};

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, showLoopControls }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [trimStartInput, setTrimStartInput] = useState("00:00:00.00");
  const [trimEndInput, setTrimEndInput] = useState("00:00:00.00");
  const [trimStartError, setTrimStartError] = useState(false);
  const [trimEndError, setTrimEndError] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      if (audio.duration === Infinity) {
        // Force browser to calculate actual duration
        audio.currentTime = 1e10;
      } else {
        const fullDuration = audio.duration * 1000;
        setDuration(fullDuration);
        if (trimEnd === 0) {
          setTrimEnd(fullDuration);
          setTrimEndInput(formatTime(fullDuration));
        }
      }
    };

    const handleDurationChange = () => {
      if (audio.duration !== Infinity) {
        const fullDuration = audio.duration * 1000;
        setDuration(fullDuration);
        if (trimEnd === 0) {
          setTrimEnd(fullDuration);
          setTrimEndInput(formatTime(fullDuration));
          audio.currentTime = trimStart / 1000;
        }
      }
    };

    const handleTimeUpdate = () => {
      const currentMs = (audio.currentTime || 0) * 1000;
      setCurrentTime(currentMs);

      if (loopEnabled && currentMs >= trimEnd) {
        audio.currentTime = trimStart / 1000;
        setCurrentTime(trimStart);
        if (isPlaying) {
          audio.play().catch((err) => console.warn("Playback failed:", err));
        }
      }
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("durationchange", handleDurationChange);
    audio.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("durationchange", handleDurationChange);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [loopEnabled, trimStart, trimEnd, isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.load(); // triggers 'loadedmetadata'
    }
  }, [src]);
  const play = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const start = () => {
      if (loopEnabled) {
        audio.currentTime = trimStart / 1000;
        setCurrentTime(trimStart);
      }

      audio
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((err) => {
          console.warn("Playback failed:", err);
        });
    };

    if (audio.readyState >= 1 && !isNaN(audio.duration)) {
      start();
    } else {
      const handleReady = () => {
        audio.removeEventListener("loadedmetadata", handleReady);
        start();
      };
      audio.addEventListener("loadedmetadata", handleReady);
      // Force metadata load by setting src and load() if not triggered
      audio.load();
    }
  };

  const pause = () => {
    audioRef.current?.pause();
    setIsPlaying(false);
  };

  const seek = (ms: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = ms / 1000;
      setCurrentTime(ms);
    }
  };

  const handleSeek = (_: Event, value: number | number[]) => {
    if (typeof value === "number") {
      seek(value);
    }
  };

  const handleTrimStartChange = (value: string) => {
    const formatted = autoFormatTime(value);
    setTrimStartInput(formatted);

    if (!isValidTimeFormat(formatted)) {
      setTrimStartError(true);
      return;
    }

    setTrimStartError(false);
    const ms = parseTime(normalizeTimeInput(formatted));
    if (!isNaN(ms) && ms < trimEnd) {
      setTrimStart(ms);
    }
  };

  const handleTrimEndChange = (value: string) => {
    const formatted = autoFormatTime(value);
    setTrimEndInput(formatted);

    if (!isValidTimeFormat(formatted)) {
      setTrimEndError(true);
      return;
    }

    setTrimEndError(false);
    const ms = parseTime(normalizeTimeInput(formatted));
    if (!isNaN(ms) && ms > trimStart && ms <= duration) {
      setTrimEnd(ms);
    }
  };

  return (
    <Box p={2} border="1px solid #ccc" borderRadius={2}>
      <audio ref={audioRef} src={src} preload="metadata" />

      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <IconButton onClick={isPlaying ? pause : play}>
          {isPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>

        <Typography variant="body2">{formatTime(currentTime)}</Typography>

        <Box sx={{ position: "relative", flex: 1 }}>
          {loopEnabled && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                height: 8,
                backgroundColor: "rgba(33, 150, 243, 0.3)",
                transform: "translateY(-50%)",
                left: `${(trimStart / duration) * 100}%`,
                width: `${Math.max(
                  ((trimEnd - trimStart) / duration) * 100,
                  1
                )}%`,
                borderRadius: 2,
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
          )}
          <Slider
            value={Math.min(currentTime, duration)}
            min={0}
            max={duration}
            onChange={handleSeek}
            step={10}
            marks={
              loopEnabled
                ? [
                    { value: trimStart, label: formatTime(trimStart) },
                    { value: trimEnd, label: formatTime(trimEnd) },
                  ]
                : []
            }
          />
        </Box>

        <Typography variant="body2">{formatTime(duration)}</Typography>
      </Box>

      {showLoopControls && (
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={loopEnabled}
                onChange={(e) => setLoopEnabled(e.target.checked)}
              />
            }
            label="Loop Trim Section"
          />

          <TextField
            label="Trim Start"
            size="small"
            defaultValue={trimStartInput}
            onChange={(e) => handleTrimStartChange(e.target.value)}
            error={trimStartError}
            helperText={trimStartError ? "Format: HH:MM:SS.mm" : ""}
            placeholder="00:00:00.00"
            inputProps={{ maxLength: 11 }}
          />

          <TextField
            label="Trim End"
            size="small"
            defaultValue={trimEndInput}
            onChange={(e) => handleTrimEndChange(e.target.value)}
            error={trimEndError}
            helperText={trimEndError ? "Format: HH:MM:SS.mm" : ""}
            placeholder="00:00:00.00"
            inputProps={{ maxLength: 11 }}
          />
        </Box>
      )}
    </Box>
  );
};

export default AudioPlayer;
